import { sectionRenderer } from '@theme/section-renderer';
import { ThemeEvents } from '@theme/events';

/**
 * Keeps the Caselle "Browse by ..." row (sections/caselle-collection-browse.liquid) in step with Shopify storefront
 * filtering. Loaded by that section on every collection page.
 *
 * The row is server-rendered from collection.filters: the active card and every card link come from the applied
 * filters. No client-side filter state is kept; sections are re-rendered for the current URL through the Section
 * Rendering API (the same mechanism facets.js uses for main-collection).
 *
 * - Filter update (sidebar inputs, filter pills, Clear all): facets.js updates the URL and re-renders main-collection;
 *   this re-renders the Browse row.
 * - Browser back/forward: Vessel's facets.js has no popstate handling, so the URL changed but no section did. When the
 *   query string differs from the last rendered one, this re-renders main-collection (grid, filters, pills) and the
 *   Browse row for the restored URL.
 */
const BROWSE_SECTION_SELECTOR = '.shopify-section.caselle-browse-section';
const SECTION_ID_PREFIX = 'shopify-section-';

let renderedSearch = window.location.search;

/** @returns {string[]} IDs of Browse sections that currently render a row */
function browseSectionIds() {
  return [...document.querySelectorAll(BROWSE_SECTION_SELECTOR)]
    .filter((section) => section.querySelector('.caselle-browse__list'))
    .map((section) => section.id.replace(SECTION_ID_PREFIX, ''));
}

/** @param {Iterable<string>} sectionIds */
function renderSections(sectionIds) {
  for (const sectionId of sectionIds) {
    sectionRenderer.renderSection(sectionId).catch(() => {});
  }
}

function handleFilterUpdate() {
  // Dispatched by facets.js after the URL has been updated and before main-collection re-renders.
  renderedSearch = window.location.search;
  renderSections(browseSectionIds());
}

function handlePopState() {
  if (window.location.search === renderedSearch) return;
  renderedSearch = window.location.search;

  const sectionIds = new Set(browseSectionIds());
  for (const facetsForm of document.querySelectorAll('facets-form-component[section-id]')) {
    const sectionId = facetsForm.getAttribute('section-id');
    if (sectionId) sectionIds.add(sectionId);
  }
  renderSections(sectionIds);
}

if (!window.caselleBrowseSync) {
  window.caselleBrowseSync = true;
  document.addEventListener(ThemeEvents.FilterUpdate, handleFilterUpdate);
  window.addEventListener('popstate', handlePopState);
}
